import { getUrlParameters } from '@/storage-helper.js';
import { postalCodeProvinceMap } from '@/constants/postal-code-provinces.js';
import { resolveProductName } from '@/constants/product-aliases.js';

/**
 * Shared lifecycle logic for the Application component.
 *
 * @param {Object} options
 * @param {import('vue').Ref} options.app - reactive app ref
 * @param {import('vue').Ref} options.config - reactive config ref
 * @param {Function} options.updateApplicationField
 * @param {Function} options.executeLoadingAnimation - bound to the component instance
 */
export function useApplicationLifecycle({ app, config, updateApplicationField }) {

    function setUrlParams(prepopFields) {
        let urlParameters = getUrlParameters();
        Object.assign(app.value, { urlParameters });
        prePopulateFields(prepopFields);
    }

    function prePopulateFields(prepopFields) {
        let skipFields = [
            'partner_id'
        ];

        try {
            const prepopFieldsJson = JSON.parse(prepopFields);
            if (prepopFieldsJson && Object.keys(prepopFieldsJson).length > 0) {
                app.value._isPrepop = true;
            }
            Object.entries(prepopFieldsJson ?? {}).forEach(entry => {
                if (!skipFields.includes(entry[0])) {
                    app.value[entry[0]] = entry[1];
                }
            });
        } catch (error) {
            console.warn('Failed to parse prepop fields:', error);
        }

        Object.entries(app.value.urlParameters).forEach(entry => {
            if (!skipFields.includes(entry[0])) {
                app.value[entry[0]] = entry[1];
            }
        });
    }

    function getLocalData() {
        // [DISABLED] localStorage read for 'comparehub_loanscanada' is not consumed anywhere.
        // To re-enable: uncomment the line below and use `storedData` to pre-populate
        // app fields (e.g. Object.assign(app.value, storedData)) so returning users
        // resume where they left off. The data shape mirrors the app object
        // (see getSafeApplicationData in @/storage-helper.js for excluded keys).
        // Also re-enable persistLocally() in useApplicationCore.js to keep writing.
        // Import: import { getLocalStorage } from '@/storage-helper.js';
        //
        // let storedData = getLocalStorage();
    }

    async function checkEarlyRegistration(applicationSettings) {
        if (app.value['email']) {
            let payload = {
                email: app.value['email'],
                password: 'fakePassword'
            };

            let validStatusCodes = [200, 401]; // 401 is to check existence only
            try {
                const res = await axios.post('/portal/member/login', payload);
                if (res.data.statusCode && validStatusCodes.includes(res.data.statusCode)) {
                    applicationSettings.isUserRegistered = true;
                }
            } catch (err) {
                console.error(err);
            }
        }
    }

    /**
     * Creates the loading animation watcher handler.
     */
    function createLoadingAnimationWatcher(executeLoadingAnimation) {
        return function (newValue) {
            // -1   => for unlimited animation when we dont know the duration of the request (max 30 seconds)
            // >0   => for animation during 'newValue' in seconds
            if (typeof newValue === 'number') {
                executeLoadingAnimation(newValue);
            }
        };
    }

    /**
     * Creates the postal code watcher handler.
     */
    function createPostalCodeWatcher() {
        return function (newValue) {
            if (newValue) {
                if (config.value.country === 'CA') {
                    let code = newValue.trim().toUpperCase().charAt(0);
                    updateApplicationField('state', postalCodeProvinceMap[code] ?? null);
                }
            }
        };
    }

    /**
     * Shared created() logic.
     */
    function onCreated(props) {
        setUrlParams(props.prepopFields);
        getLocalData(); // [DISABLED] currently a no-op — see getLocalData() above
        app.value['product_name'] = resolveProductName(props.product);
    }

    /**
     * Shared mounted() logic.
     */
    function onMounted(props, projectBrandRef) {
        updateApplicationField('forceSmsVerification', props.forceSmsVerification);
        const projectBrand = window.App?.project_brand ?? null;
        projectBrandRef.value = projectBrand;
        updateApplicationField('experiments', window.App?.experiments ?? null);
        updateApplicationField('projectBrandKey', projectBrand?.key ?? 'lc');
        updateApplicationField('projectBrandCountry', props.country);
        updateApplicationField('smsBypassEnabled', window.App?.sms_bypass_enabled ?? false);
        updateApplicationField('opt_in', true);
    }

    return {
        setUrlParams,
        prePopulateFields,
        getLocalData,
        checkEarlyRegistration,
        createLoadingAnimationWatcher,
        createPostalCodeWatcher,
        onCreated,
        onMounted,
    };
}
